import { useState, type FormEvent } from 'react';
import { api, normalizeRoomCode } from '../lib/api';
import { navigate } from '../lib/router';
import { hostKeyFor, save } from '../lib/storage';
import { createRoomErrorMessage } from '../lib/apiErrors';
import { AccountMenu } from '../components/AccountMenu';
import { CamIcon, ChatIcon, LockIcon, ScreenIcon, VideoPlusIcon } from '@archik/call';

export function Home() {
  const [joinInput, setJoinInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const joinCode = normalizeRoomCode(joinInput);
  const invalidJoin = joinInput.trim().length > 0 && !joinCode;

  async function create() {
    setCreating(true);
    setError(null);
    try {
      const { code, hostKey } = await api.createRoom();
      save(hostKeyFor(code), hostKey);
      navigate(`/${code}`);
    } catch (e) {
      setError(createRoomErrorMessage(e));
      setCreating(false);
    }
  }

  function join(e: FormEvent) {
    e.preventDefault();
    if (joinCode) navigate(`/${joinCode}`);
  }

  return (
    <div className="page">
      <header className="top-bar">
        <a className="brand" href="/">
          <span className="brand-mark" aria-hidden><CamIcon width={18} height={18} /></span>
          Archik Meet
        </a>
        <AccountMenu />
      </header>

      <main className="home">
        <section className="home-copy">
          <p className="eyebrow">Быстрые встречи в браузере</p>
          <h1>Созвонитесь<br />по одной ссылке</h1>
          <p className="lead">
            Создайте встречу и отправьте ссылку. Участникам не нужны ни аккаунт, ни установка.
          </p>

          <div className="home-actions">
            <button className="btn primary lg" onClick={create} disabled={creating}>
              <VideoPlusIcon width={20} height={20} />
              {creating ? 'Создаём…' : 'Новая встреча'}
            </button>
            <form className="join-form" onSubmit={join}>
              <div className="join-field">
                <input
                  aria-label="Код или ссылка на встречу"
                  aria-invalid={invalidJoin}
                  aria-describedby={invalidJoin ? 'join-hint' : undefined}
                  placeholder="Введите код или ссылку"
                  value={joinInput}
                  onChange={(e) => setJoinInput(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                {invalidJoin && <span className="field-hint error" id="join-hint">Код содержит 12 латинских букв, например abcd-efgh-ijkl.</span>}
              </div>
              <button className="btn ghost" disabled={!joinCode}>Войти</button>
            </form>
          </div>
          {error && <p className="error" role="alert">{error}</p>}

          <ul className="feature-list">
            <li><CamIcon width={18} height={18} /> Видео и чистый звук</li>
            <li><ScreenIcon width={18} height={18} /> Демонстрация экрана</li>
            <li><ChatIcon width={18} height={18} /> Чат во время встречи</li>
            <li><LockIcon width={18} height={18} /> Чат не сохраняется, запись — только если её включат</li>
          </ul>
        </section>

        <section className="home-art" aria-hidden>
          <div className="art-glow" />
          <div className="product-card">
            <span className="product-kicker">ARCHIK MEET</span>
            <strong>Одна ссылка.<br />Сразу в разговор.</strong>
            <p>Без регистрации и установки</p>
            <div className="avatar-row">
              <span className="avatar a1">А</span>
              <span className="avatar a2">М</span>
              <span className="avatar a3">К</span>
              <span className="live-dot">в эфире</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
